(function () {
  'use strict';

  // Delay between cart-change event and orphan check, to let the cart settle
  const CLEANUP_DEBOUNCE_MS = 300;

  let isCleaningUp = false;
  let cleanupTimer = null;

  // Build an `updates` object from a list of line item keys, mapping each to 0.
  // Used by /cart/update.js to remove multiple lines in one round trip.
  function buildRemovalUpdates(keys) {
    return keys.reduce((acc, key) => {
      acc[key] = 0;
      return acc;
    }, {});
  }

  // Send a single batched removal to /cart/update.js
  async function batchRemoveLines(keys) {
    if (keys.length === 0) return;
    await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates: buildRemovalUpdates(keys) }),
    });
  }

  // Click handler for the parent's Remove link.
  // Removes parent + all its bundle children in a single request, eliminating
  // the slow page-reload + sequential orphan-cleanup cycle.
  async function onParentRemoveClick(event) {
    const removeBtn = event.target.closest('.line-item--bundle-parent .line-item__remove-button');
    if (!removeBtn) return;

    const row = removeBtn.closest('[data-key]');
    if (!row) return;

    const parentKey = row.dataset.key;
    const bundleId = row.dataset.bundleId;
    if (!parentKey) return;

    event.preventDefault();
    event.stopPropagation();

    // Collect parent + all child rows that share this bundle_id
    const rowsToRemove = [row];
    const keysToRemove = [parentKey];
    if (bundleId) {
      document
        .querySelectorAll(`.line-item--bundle-child[data-bundle-id="${bundleId}"]`)
        .forEach((childRow) => {
          if (!childRow.dataset.key) return;
          rowsToRemove.push(childRow);
          keysToRemove.push(childRow.dataset.key);
        });
    }

    // Optimistically blur the entire bundle (parent + children) so the UI feels instant
    rowsToRemove.forEach((r) => {
      r.style.opacity = '0.4';
      r.style.pointerEvents = 'none';
    });

    try {
      await batchRemoveLines(keysToRemove);
      await refreshFocalCart();

      if (window.location.pathname === '/cart') {
        window.location.reload();
      }
    } catch (e) {
      // Restore visual state on error
      rowsToRemove.forEach((r) => {
        r.style.opacity = '';
        r.style.pointerEvents = '';
      });
    }
  }

  // Background safety net: detect any orphan children (e.g. left over from
  // a prior session, or from a manual cart edit that bypassed our handler)
  // and remove them in a single batch.
  async function cleanOrphanedBundleChildren() {
    if (isCleaningUp) return;
    isCleaningUp = true;

    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();

      const parentBundleIds = new Set();
      cart.items.forEach((item) => {
        if (!item.properties) return;
        if (item.properties._bundle_role === 'parent' && item.properties._bundle_id) {
          parentBundleIds.add(item.properties._bundle_id);
        }
      });

      const orphans = [];
      cart.items.forEach((item) => {
        if (!item.properties) return;
        const role = item.properties._bundle_role;
        const bundleId = item.properties._bundle_id;

        if (role === 'child' && bundleId && !parentBundleIds.has(bundleId)) {
          orphans.push(item.key);
          return;
        }
        if (item.properties._bundle && !role) {
          orphans.push(item.key);
        }
      });

      if (orphans.length === 0) {
        isCleaningUp = false;
        return;
      }

      await batchRemoveLines(orphans);
      await refreshFocalCart();

      if (window.location.pathname === '/cart') {
        window.location.reload();
      }
    } catch (e) {
      // Silently fail — cleanup will retry on next cart change
    }

    isCleaningUp = false;
  }

  // Dispatch the events that <cart-count> and <cart-drawer> listen on
  async function refreshFocalCart() {
    try {
      const cartUrl = (window.themeVariables
        && window.themeVariables.routes
        && window.themeVariables.routes.cartUrl) || '/cart';
      const sectionId = 'mini-cart';

      const [cartContent, miniCartHtml] = await Promise.all([
        fetch(cartUrl + '.js').then((r) => r.json()),
        fetch(cartUrl + '?section_id=' + sectionId).then((r) => r.text()),
      ]);

      cartContent.sections = { 'mini-cart': miniCartHtml };

      document.documentElement.dispatchEvent(new CustomEvent('cart:updated', {
        bubbles: true,
        detail: { cart: cartContent },
      }));

      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
        bubbles: true,
        detail: { cart: cartContent, openMiniCart: false },
      }));
    } catch (e) {
      // Silently fail
    }
  }

  function scheduleCleanup() {
    if (isCleaningUp) return;
    if (cleanupTimer) clearTimeout(cleanupTimer);
    cleanupTimer = setTimeout(cleanOrphanedBundleChildren, CLEANUP_DEBOUNCE_MS);
  }

  // Run cleanup once on page load (catches stale orphans from prior sessions)
  cleanOrphanedBundleChildren();

  // Intercept parent remove clicks for instant batched removal.
  // Capture phase so we run before the link's default navigation.
  document.addEventListener('click', onParentRemoveClick, true);

  // Background safety net: listen for Focal's cart-update events.
  document.documentElement.addEventListener('cart:updated', scheduleCleanup);
  document.documentElement.addEventListener('cart:refresh', scheduleCleanup);
})();
